import { Container, css, keyframes, Paper, styled, Typography } from '@mui/material';
import { palette } from 'assets/styles/colors';
import { theme } from 'assets/themes/zizzi';
import SurpriseBubbleGraphic from 'assets/images/graphic-surprise-bubble.svg';
import MobileWave from 'assets/images/graphic-surprise-wave-mobile.svg';
import DesktopWave from 'assets/images/graphic-surprise-wave-desktop.svg';
import SurpriseBG from 'assets/images/img-cyclical-background.jpg';
import QuestionMark from 'assets/icons/QuestionMark';
import WheelClicker from './SpinToWinAnimation/WheelClicker';
import Wheel from './SpinToWinAnimation/Wheel';

interface AnimationProps {
  shouldAnimate: boolean;
}

interface TextProps {
  alternative: string;
}

const textColor = palette.sunshine[900];
const subtitleColor = theme.palette.white;
const subtitleColorLG = theme.palette.primary.main;
const pageBgColor = theme.palette.white;
const animationSettings = '3s cubic-bezier(0.62, -0.04, 0, 1.23) forwards';

const animateSurpriseSpinner = keyframes`
  0% { transform: scale(7) translate(0%, 45%) rotate(0deg); }
  20% { transform: scale(7) translate(0%, 45%) rotate(10deg); }
  100% { transform: scale(7) translate(0%, 45%) rotate(-360deg); }
`;

const animateWheelContainer = keyframes`
100% { transform: translate(0, 25%) scale(1.75); }
`;

const animateWheelContainerForDesktop = keyframes`
100% { transform: translate(0, 25%) scale(1.8);}
`;

const animateWheelSpin = keyframes`
  0% { transform: rotate(0deg); }
  10% { transform: rotate(10deg); }
  70% { transform: rotate(-360deg); }
  100% { transform: rotate(-360deg); }
`;

const animateWheelClicker = keyframes`
  0%, 75%, 100% { transform:  rotate(0deg); }
  5%, 10%, 16%, 23%, 31%, 40%, 50%, 63%, { transform:  rotate(5deg); }
  7.5%, 13%, 20.5%, 27%, 35.5%, 45%, 55.5%, 67% { transform:  rotate(-5deg); } 
`;

export const StyledContainer = styled(Container)`
  position: relative;
  ${theme.breakpoints.up('md')} {
    margin: auto;
    max-width: 880px;
  }
`;

export const StyledPaper = styled(Paper)`
  background: url(${SurpriseBG});
  border-radius: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding: 0;

  ${theme.breakpoints.up('sm')} {
    border-radius: ${theme.radius?.lg};
  }
`;

export const Header = styled('div')`
  align-items: center;
  background: url(${MobileWave}) bottom center/auto no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 170px;
  padding-bottom: 3.5rem;

  ${theme.breakpoints.up('sm')} {
    background: url(${DesktopWave}) bottom center no-repeat;
    border-radius: 16px 16px 0 0;
    min-height: 290px;
    padding-bottom: 4rem;
  }

  h2 {
    display: none;

    ${theme.breakpoints.up('sm')} {
      display: block;
    }
  }
`;

export const Title = styled(Typography)<TextProps>`
  font-size: 32px;
  margin-bottom: 0;
  color: ${textColor};
  text-align: center;
  ${({ alternative }) =>
    alternative === 'true' &&
    css`
      font-size: 26px;
      padding: 2.5rem 2rem 0.5rem;
    `};

  ${theme.breakpoints.up('sm')} {
    margin-bottom: 1rem;
    ${({ alternative }) =>
      alternative === 'true' &&
      css`
        margin-bottom: 0;
        font-size: 50px;
      `};
  }
`;

export const SubTitle = styled(Typography)`
  color: ${subtitleColor};
  font-size: 30px;
  margin-bottom: 0;
  max-width: 70%;
  text-align: center;

  ${theme.breakpoints.up('sm')} {
    color: ${subtitleColorLG};
    max-width: none;
  }

  @media (max-width: 349px) {
    font-size: 27px;
  }
`;

export const Content = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-start;
  padding: 1rem;
  overflow: hidden;
`;

export const SurpriseBubble = styled('div')`
  align-items: center;
  background: url(${SurpriseBubbleGraphic}) no-repeat center center;
  background-size: contain;
  display: flex;
  flex-direction: column;
  height: 180px;
  justify-content: center;
  margin-top: -1rem;

  ${theme.breakpoints.up('sm')} {
    display: none;
  }
`;

export const SurpriseSpinner = styled('div')`
  align-self: center;
  display: flex;
  height: 220px;

  ${theme.breakpoints.up(460)} {
    overflow: hidden;
    width: 60%;
  }
`;

export const SurpriseSpinnerAnimation = styled(QuestionMark)<AnimationProps>`
  animation: ${({ shouldAnimate }) =>
    shouldAnimate &&
    css`
      ${animateSurpriseSpinner} ${animationSettings};
    `};
  height: 150px;
  margin-bottom: 2rem;
  transform: scale(7) translate(0%, 45%);
  width: 600px;
`;

export const WheelContainerAnimation = styled('div')<AnimationProps>`
  position: relative;
  display: flex;
  margin-top: 7px;
  transition: ease-out;
  animation: ${({ shouldAnimate }) =>
    shouldAnimate &&
    css`
      ${animateWheelContainer} 3s 1 forwards 3s;
    `};

  ${theme.breakpoints.up('md')} {
    margin-top: 12px;
    animation: ${({ shouldAnimate }) =>
      shouldAnimate &&
      css`
        ${animateWheelContainerForDesktop} 3s 1 forwards 3s;
      `};
  }
`;

export const WheelClickerAnimation = styled(WheelClicker)<AnimationProps>`
  position: absolute;
  left: 40%;
  margin: 0 auto;
  z-index: 1;
  transition: ease-out;
  animation: ${({ shouldAnimate }) =>
    shouldAnimate &&
    css`
      ${animateWheelClicker} 6s 1s 1;
    `};
`;

export const WheelAnimation = styled(Wheel)<AnimationProps>`
  margin: 0 auto;
  transition: cubic-bezier(0, 1.01, 0, 1.02);
  animation: ${({ shouldAnimate }) =>
    shouldAnimate &&
    css`
      ${animateWheelSpin} 8s 1;
    `};
`;

export const Text = styled(Typography)`
  color: ${pageBgColor};
  margin-bottom: 0.35rem;
  text-align: center;
`;

export const Footer = styled('div')`
  align-items: center;
  background: ${pageBgColor};
  border-radius: 12px 12px 0 0;
  bottom: 0;
  box-shadow: 0px -1px 5px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  padding: 18px;
  position: relative;
  width: 100%;

  ${theme.breakpoints.up('sm')} {
    border-radius: 12px 12px 16px 16px;
  }
`;
